import { cn } from "@jornada/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * Jornada brand mark + wordmark.
 *
 * Identity: a "workday arc" — the sun's path over a horizon, with a node at the
 * apex. It reads as a *jornada* (a day of work) tracked over time: calm,
 * data-forward, and honest — productivity with trust, never surveillance.
 *
 * THEME-SAFE BY CONSTRUCTION (gotcha confirmed in sibling clones): drawing mark
 * parts with `text-accent` / raw accent on the page surface makes them vanish
 * under pale/custom themes. Instead the entire mark is drawn with `currentColor`
 * inside a SOLID `bg-accent` chip whose text color is `text-accent-foreground` —
 * a pair the theme engine GUARANTEES to contrast. The wordmark uses
 * `text-foreground` (always legible over the app background), so it never
 * depends on the accent hue for legibility. "Jornada" is never split into two
 * tinted spans (it would strand "nada" — Spanish for "nothing").
 */
function BrandMark(props: { px: number }) {
  const glyph = () => Math.round(props.px * 0.64);
  return (
    <div
      class="grid place-items-center rounded-2xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5"
      style={{ width: `${props.px}px`, height: `${props.px}px` }}
      aria-hidden="true"
    >
      {/* Workday arc: horizon baseline + day dome + apex node, drawn entirely
       * with currentColor (inherited from text-accent-foreground) so contrast
       * is guaranteed under any theme. */}
      <svg
        width={glyph()}
        height={glyph()}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="shrink-0"
      >
        {/* Horizon (the working baseline) */}
        <path d="M4 16.5H20" />
        {/* The day's arc, from start to end of shift */}
        <path d="M6.5 16.5a5.5 5.5 0 0 1 11 0" />
        {/* Apex node — the tracked moment */}
        <circle cx="12" cy="10.4" r="1.9" fill="currentColor" stroke="none" />
      </svg>
    </div>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <BrandMark px={32} />
          <span class="text-xl font-extrabold tracking-tight text-foreground">Jornada</span>
        </>
      ) : (
        <>
          <div class="mb-2 transition-transform hover:scale-105 active:scale-95 duration-300">
            <BrandMark px={64} />
          </div>
          <h1 class="text-3xl font-extrabold tracking-tight text-foreground">Jornada</h1>
        </>
      )}
    </div>
  );
}
